import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { ScholarshipService } from '../service/scholarship.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { ScholarshipActions } from './scholarship.actions';
import { DeleteStudentScholarshipData, StudentScholarshipData } from '../models/student-scholarship-data.model';
import { FamilyActions } from '../../family/store/family/family.actions';
import { patch } from '@ngxs/store/operators';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';

export class ScholarshipStateModel {
  studentScholarshipForm: {
    model: StudentScholarshipData;
  };
  public isLoadingScholarship: boolean;
  public error: string;
}

const defaults = {
  studentScholarshipForm: {
    model: null,
  },
  isLoadingScholarship: false,
  error: null,
};

@State<ScholarshipStateModel>({
  name: 'scholarships',
  defaults,
})
@Injectable()
export class ScholarshipState {
  constructor(
    private scholarshipService: ScholarshipService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(ScholarshipActions.AddStudentScholarship)
  addStudentScholarship(ctx: StateContext<ScholarshipStateModel>, { user }: ScholarshipActions.AddStudentScholarship
  ) {
    const state = ctx.getState();
    let studentScholarshipData = state.studentScholarshipForm.model;
    this.alertService
      .confirm(
        this.localizationService.translate('labels.student-scholarship'),
        this.localizationService.translate(
          'scholarships.studentScholarshipAddConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'scholarships.studentScholarshipAddConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingScholarship: true });
          return this.scholarshipService
            .addStudentScholarship(studentScholarshipData, user)
            .subscribe({
              next: (studentScholarshipId) =>
                ctx.dispatch(
                  new ScholarshipActions.AddStudentScholarshipSuccess(
                    studentScholarshipId
                  )
                ),
              error: (error) => {
                ctx.dispatch(new ScholarshipActions.Error(error));
              },
            });
        } else {
          return null;
        }
      });
  }

  @Action(ScholarshipActions.AddStudentScholarshipSuccess)
  addStudentScholarshipSuccess(
    ctx: StateContext<ScholarshipStateModel>,
    { studentScholarshipId }: ScholarshipActions.AddStudentScholarshipSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingScholarship: false,
        studentScholarshipForm: patch({
          model: patch({
            studentScholarshipId: studentScholarshipId,
          }),
        }),
      })
    );
  }

  @Action(ScholarshipActions.DeleteStudentScholarship)
  deleteStudentScholarship( ctx: StateContext<ScholarshipStateModel>, { studentId, studentScholarshipId, user }: ScholarshipActions.DeleteStudentScholarship
  ) {
    this.alertService
      .confirm(
        this.localizationService.translate('labels.student-scholarship'),
        this.localizationService.translate(
          'scholarships.studentScholarshipDeleteConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'scholarships.studentScholarshipDeleteConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingScholarship: true });
          let deleteStudentScholarshipData = new DeleteStudentScholarshipData();
          deleteStudentScholarshipData.studentId = studentId;
          deleteStudentScholarshipData.studentScholarshipId = studentScholarshipId;

          return this.scholarshipService
            .deleteStudentScholarship(deleteStudentScholarshipData, user)
            .subscribe({
              next: () =>
                ctx.dispatch(
                  new ScholarshipActions.DeleteStudentScholarshipSuccess(
                    studentId, studentScholarshipId
                  )
                ),
              error: (error) => {
                ctx.dispatch(new ScholarshipActions.Error(error));
              },
            });
        } else {
          return null;
        }
    });
  }

  @Action(ScholarshipActions.DeleteStudentScholarshipSuccess)
  deleteStudentScholarshipSuccess(
    ctx: StateContext<ScholarshipStateModel>,
    { studentId, studentScholarshipId }: ScholarshipActions.DeleteStudentScholarshipSuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingScholarship: false,
    });
    this.store.dispatch(
      new FamilyActions.DeleteStudentScholarship(studentId, studentScholarshipId)
    );
  }

  @Action(ScholarshipActions.SetStudentScholarshipDefaultState)
  setDefaultState(
    ctx: StateContext<ScholarshipStateModel>,
    {}: ScholarshipActions.SetStudentScholarshipDefaultState
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state, studentScholarshipForm: defaults.studentScholarshipForm
    });
  }

  @Action(ScholarshipActions.Error)
  onErrorOcurred(
    ctx: StateContext<ScholarshipStateModel>,
    { error }: ScholarshipActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoadingScholarship: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private getTitle() {
    return this.localizationService.translate('labels.student-scholarship');
  }
}
